// useBotNotifications.ts — bot-notifications design (docs/superpowers/specs/
// 2026-08-21-bot-notifications-design.md §Dashboard), Task 5. `GET /api/bots/:botId/
// notifications` already returns exactly the rows the caller may see (Task 4, M1: the SERVER
// filters `members` to the caller's own row for `role === 'member'`), so this hook carries no
// role logic of its own — every row the query returns is a row the caller is allowed to edit.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BotNotificationsDto, NotificationPrefsDto } from '@repo/shared-types';
import { apiFetch } from '../lib/api';

export function notificationsQueryKey(botId: string | undefined) {
  return ['bots', botId, 'notifications'] as const;
}

export function useBotNotifications(botId: string | undefined) {
  return useQuery({
    queryKey: notificationsQueryKey(botId),
    queryFn: () => apiFetch<BotNotificationsDto>(`/api/bots/${botId}/notifications`),
    enabled: !!botId,
  });
}

interface PatchPrefInput {
  userId: string;
  patch: Partial<NotificationPrefsDto>;
}

/** `PATCH /api/bots/:botId/notifications/:userId` — one cell of the matrix (`NotificationsCard`
 * calls this once per Switch/Select change, body = `{ [field]: value }`). Optimistic: the cell
 * flips in the cache the instant it's toggled (`onMutate`), rolls back to the pre-mutate snapshot
 * on failure (`onError`), and — success or failure — refetches on settle so the cache always ends
 * up agreeing with the server's actual row, not just the guessed value. */
export function usePatchNotificationPref(botId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = notificationsQueryKey(botId);

  return useMutation({
    mutationFn: ({ userId, patch }: PatchPrefInput) =>
      apiFetch<{ prefs: NotificationPrefsDto }>(`/api/bots/${botId}/notifications/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }),
    onMutate: async ({ userId, patch }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BotNotificationsDto>(queryKey);
      if (previous) {
        queryClient.setQueryData<BotNotificationsDto>(queryKey, {
          members: previous.members.map((member) =>
            member.userId === userId ? { ...member, prefs: { ...member.prefs, ...patch } } : member,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
